import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FieldValues, useForm, UseFormSetError, useWatch } from "react-hook-form";
import { boolean, number, object, string } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { DevTool } from "@hookform/devtools";
import { CompanyType as CompanyTypeEnum } from "@green0meter/api-client";
import { t } from "i18next";

import EntityEditorSkeleton from "./EntityEditor.skeleton";
import EntityName from "./components/EntityName";
// import Features from "./components/Features";
import Currency from "./components/Currency";
// import Group from "./components/Group";
import AnnualTurnover from "./components/AnnualTurnover";
// import EuTaxonomyAssesment from "./components/EuTaxonomyAssesment";
import Vat from "./components/Vat";
import CompanyType from "./components/CompanyType";
import OwnershipShare from "./components/OwnershipShare";
import RevenueAndExpenses from "./components/RevenueAndExpenses";
import IsExternal from "./components/IsExternal";
import { entityTypesFields } from "./EntityEditor.constants";

import { NaceDivision, NaceSection } from "@components/common/Nace";
import { useCreateCompany, useGetCompanyDetail, useUpdateCompany } from "@api";
import { Dialog } from "@components/common";
import { Button, Grid, Typography } from "@components/base";
import { EntityTypesFields } from "@type/Entities";
import { useUserStore } from "@store/user";

export type CreateUpdateEntityFormValues = {
  name: string;
  companyType: CompanyTypeEnum;
  vat: string;
  currency: string;
  revenueAndExpenses?: string | null;
  group?: string | null;
  annualTurnover?: number | null;
  isExternal?: boolean | null;
  assetType?: string | null;
  ownershipShare?: number | null;
  naceSectionId?: string | null;
  naceDivisionId?: string | null;
};

const schema = object({
  name: string().max(255).required(t("validation.required")),
  companyType: number<CompanyTypeEnum>().required(t("validation.required")),
  vat: string().max(255).required(t("validation.required")),
  currency: string().max(255).required(t("validation.required")),
  isExternal: boolean().nullable().notRequired(),

  ownershipShare: number()
    .nullable()
    .when("companyType", {
      is: (val: CompanyTypeEnum) =>
        [CompanyTypeEnum.Subsidiary, CompanyTypeEnum.Asset].includes(val),
      then: (schema) =>
        schema
          .required(t("validation.required"))
          .min(1, t("validation.ownershipShareMin"))
          .max(100, t("validation.ownershipShareMax")),
      otherwise: (schema) => schema.notRequired(),
    }),

  // assetType: string()
  //   .nullable()
  //   .when("companyType", {
  //     is: CompanyTypeEnum.Asset,
  //     then: (schema) => schema.required(t("validation.required")),
  //     otherwise: (schema) => schema.notRequired(),
  //   }),

  annualTurnover: number()
    .nullable()
    .when("companyType", {
      is: CompanyTypeEnum.Supplier,
      then: (schema) => schema.required(t("validation.required")),
      otherwise: (schema) => schema.notRequired(),
    }),

  naceSectionId: string()
    .nullable()
    .when("companyType", {
      is: CompanyTypeEnum.Supplier,
      then: (schema) => schema.required(t("validation.required")),
      otherwise: (schema) => schema.notRequired(),
    }),

  naceDivisionId: string()
    .nullable()
    .when("companyType", {
      is: CompanyTypeEnum.Supplier,
      then: (schema) => schema.required(t("validation.required")),
      otherwise: (schema) => schema.notRequired(),
    }),
}).required();

interface EntityEditor {
  isOpen: boolean;
  onClose: () => void;
  companyId?: string;
}

const EntityEditor: FC<EntityEditor> = ({ isOpen, onClose, companyId }) => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const [companyTypeFields, setCompanyTypeFields] =
    useState<EntityTypesFields[keyof EntityTypesFields]>();

  const {
    updateCompany,
    updateCompanyEnabled,
    isSuccessUpdateCompany,
    isPendingUpdateCompany,
    isErrorUpdateCompany,
  } = useUpdateCompany(user);
  const { companyDetail, isSuccessGetCompanyDetail } = useGetCompanyDetail(
    {
      companyId,
    },
    user,
  );

  const { control, reset, handleSubmit, setValue, formState, setError } =
    useForm<CreateUpdateEntityFormValues>({
      defaultValues: {
        companyType: CompanyTypeEnum.Subsidiary,
        currency: "",
      },
      resolver: yupResolver(schema),
      mode: "onChange",
    });
  const { createCompany, isSuccessCreateCompany, isPendingCreateCompany } =
    useCreateCompany(user, setError as UseFormSetError<FieldValues>);

  const { isValid } = formState;

  const companyType = useWatch({ name: "companyType", control });

  const handleClose = (): void => {
    reset();
    onClose();
  };

  const handleValid = async (data: FieldValues): Promise<void> => {
    const { name, companyType } = data;

    if (companyId) {
      updateCompany({
        companyId,
        data: {
          name,
          companyType,
          vat: data.vat,
          currencyId: data.currency,
          ownershipSharePercentage: data.ownershipShare,
          ...data,
        },
      });
    } else {
      createCompany({
        name,
        companyType,
        vat: data.vat,
        currencyId: data.currency,
        ownershipSharePercentage: data.ownershipShare,
        ...data,
      });
    }
  };

  useEffect(() => {
    if (companyType) {
      const fields = entityTypesFields[companyType];

      setCompanyTypeFields(fields);
    }
  }, [companyType]);

  useEffect(() => {
    if (isSuccessUpdateCompany || isSuccessCreateCompany) {
      reset();
      onClose();
    }
  }, [isSuccessUpdateCompany, isSuccessCreateCompany]);

  useEffect(() => {
    reset();
    if (companyDetail) {
      setValue("name", companyDetail.name);
      setValue("vat", companyDetail.vat);
      setValue("companyType", companyDetail.companyType as CompanyTypeEnum);
      setValue("currency", companyDetail.currencyId);

      switch (companyDetail.companyType) {
        case CompanyTypeEnum.Subsidiary:
          setValue("ownershipShare", companyDetail.ownershipShare);
          break;

        case CompanyTypeEnum.Asset:
          setValue("ownershipShare", companyDetail.ownershipShare);
          // TODO: tbd setValue("assetType", companyDetail.assetType);
          break;

        case CompanyTypeEnum.Supplier:
          setValue("isExternal", companyDetail.isExternal);
          setValue("annualTurnover", companyDetail.annualTurnover);
          setValue("naceSectionId", companyDetail.naceSectionId);
          setValue("naceDivisionId", companyDetail.naceDivisionId);
          break;

        default:
          break;
      }
    }
  }, [companyDetail]);

  const naceSection = useWatch({ control, name: "naceSectionId" });

  return (
    updateCompanyEnabled && (
      <Dialog
        isOpen={isOpen}
        onClose={handleClose}
        title={companyDetail ? t("Edit entity") : t("Create entity")}
      >
        {companyId && !isSuccessGetCompanyDetail ? (
          <EntityEditorSkeleton />
        ) : (
          <>
            <form onSubmit={handleSubmit(handleValid)}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <EntityName control={control} />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Vat control={control} />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <CompanyType control={control} />
                </Grid>
                {companyTypeFields && "currency" in companyTypeFields && (
                  <Grid size={{ xs: 12 }}>
                    <Currency control={control} />
                  </Grid>
                )}
                {companyTypeFields && "revenueAndExpenses" in companyTypeFields && (
                  <Grid size={{ xs: 12 }}>
                    <RevenueAndExpenses control={control} />
                  </Grid>
                )}
                {companyTypeFields && "naceSectionId" in companyTypeFields && (
                  <Grid size={{ xs: 12 }}>
                    <NaceSection control={control} />
                  </Grid>
                )}
                {companyTypeFields &&
                  "naceDivisionId" in companyTypeFields &&
                  naceSection && (
                    <Grid size={{ xs: 12 }}>
                      <NaceDivision naceSectionId={naceSection} control={control} />
                    </Grid>
                  )}
                {companyTypeFields && "annualTurnover" in companyTypeFields && (
                  <Grid size={{ xs: 12 }}>
                    <AnnualTurnover control={control} />
                  </Grid>
                )}
                {companyTypeFields && "ownershipShare" in companyTypeFields && (
                  <Grid size={{ xs: 12 }}>
                    <OwnershipShare control={control} />
                  </Grid>
                )}
                {companyTypeFields && "isExternal" in companyTypeFields && (
                  <Grid size={{ xs: 12 }}>
                    <IsExternal control={control} />
                  </Grid>
                )}
              </Grid>
              <Grid size={{ xs: 12 }} marginTop={2}>
                <Button
                  type="submit"
                  disabled={!isValid}
                  variant="contained"
                  isLoading={isPendingUpdateCompany || isPendingCreateCompany}
                  fullWidth
                  size="large"
                >
                  {!companyId && t("entitiesManagement.entityEditor.create")}
                  {companyId && t("entitiesManagement.entityEditor.save")}
                </Button>
              </Grid>
              {isErrorUpdateCompany && (
                <Grid size={{ xs: 12 }} marginTop={2}>
                  <Typography color="error">
                    {t("entitiesManagement.entityEditor.updateError")}
                  </Typography>
                </Grid>
              )}
            </form>
            <DevTool control={control} />
          </>
        )}
      </Dialog>
    )
  );
};

export default EntityEditor;
